import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useUpdateOrderingSettings,
  type DeliveryCoverageSettings,
  type DeliveryOriginSettings,
} from "@/hooks/use-orders";

type DeliveryConfigCardProps = {
  deliveryEnabled: boolean;
  origin: DeliveryOriginSettings | null;
  coverage: DeliveryCoverageSettings | null;
};

const ORIGIN_LAT_LNG_PRESENT = (
  value: string | number | null | undefined,
): value is string | number => value !== null && value !== undefined && value !== "";

export function DeliveryConfigCard({ deliveryEnabled, origin, coverage }: DeliveryConfigCardProps) {
  const { t } = useTranslation();
  const update = useUpdateOrderingSettings();
  const [latText, setLatText] = useState(origin?.lat?.toString() ?? "");
  const [lngText, setLngText] = useState(origin?.lng?.toString() ?? "");
  const [line1, setLine1] = useState(origin?.line1 ?? "");
  const [line2, setLine2] = useState(origin?.line2 ?? "");
  const [city, setCity] = useState(origin?.city ?? "");
  const [country, setCountry] = useState(origin?.country ?? "UY");
  const [radiusText, setRadiusText] = useState(coverage?.radiusM?.toString() ?? "");

  useEffect(() => {
    setLatText(origin?.lat?.toString() ?? "");
    setLngText(origin?.lng?.toString() ?? "");
    setLine1(origin?.line1 ?? "");
    setLine2(origin?.line2 ?? "");
    setCity(origin?.city ?? "");
    setCountry(origin?.country ?? "UY");
    setRadiusText(coverage?.radiusM?.toString() ?? "");
  }, [origin, coverage]);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const lat = Number(latText);
    const lng = Number(lngText);
    const radiusM = Number(radiusText);
    if (
      !Number.isFinite(lat) ||
      !Number.isFinite(lng) ||
      !line1.trim() ||
      !Number.isFinite(radiusM)
    ) {
      toast.error(
        t("orders.settings.deliveryConfigInvalid", {
          defaultValue: "Revisá origen y radio. Todos los campos son obligatorios.",
        }),
      );
      return;
    }
    try {
      await update.mutateAsync({
        deliveryOrigin: {
          lat,
          lng,
          line1: line1.trim(),
          line2: line2.trim() || null,
          city: city.trim() || null,
          country: country.trim() || "UY",
        },
        deliveryCoverage: { radiusM: Math.round(radiusM) },
      });
      toast.success(
        t("orders.settings.deliveryConfigSaved", {
          defaultValue: "Configuración de envíos guardada",
        }),
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    }
  };

  const clear = async () => {
    try {
      await update.mutateAsync({
        clearDeliveryOrigin: true,
        clearDeliveryCoverage: true,
      });
      toast.success(
        t("orders.settings.deliveryConfigCleared", {
          defaultValue: "Origen y cobertura de envío eliminados",
        }),
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    }
  };

  const isConfigured =
    ORIGIN_LAT_LNG_PRESENT(origin?.lat) &&
    ORIGIN_LAT_LNG_PRESENT(origin?.lng) &&
    Boolean(origin?.line1) &&
    coverage?.radiusM !== null &&
    coverage?.radiusM !== undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t("orders.settings.deliveryConfigTitle", {
            defaultValue: "Origen y cobertura de envíos",
          })}
        </CardTitle>
        <CardDescription>
          {deliveryEnabled
            ? t("orders.settings.deliveryConfigDescription", {
                defaultValue:
                  "Configurá el origen operativo desde donde salen los pedidos y el radio de cobertura. Sin esto, los envíos quedan sin configurar aunque estén habilitados.",
              })
            : t("orders.settings.deliveryConfigRequiresDelivery", {
                defaultValue: "Habilitá los envíos para poder definir el origen y la cobertura.",
              })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={submit}>
          <fieldset className="space-y-3" disabled={!deliveryEnabled || update.isPending}>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="deliveryOriginLat">
                  {t("orders.settings.deliveryOriginLat", { defaultValue: "Latitud" })}
                </Label>
                <Input
                  id="deliveryOriginLat"
                  inputMode="decimal"
                  onChange={(event) => setLatText(event.target.value)}
                  placeholder="-34.9011"
                  value={latText}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deliveryOriginLng">
                  {t("orders.settings.deliveryOriginLng", { defaultValue: "Longitud" })}
                </Label>
                <Input
                  id="deliveryOriginLng"
                  inputMode="decimal"
                  onChange={(event) => setLngText(event.target.value)}
                  placeholder="-56.1645"
                  value={lngText}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="deliveryOriginLine1">
                {t("orders.settings.deliveryOriginLine1", {
                  defaultValue: "Dirección (calle y número)",
                })}
              </Label>
              <Input
                id="deliveryOriginLine1"
                onChange={(event) => setLine1(event.target.value)}
                value={line1}
              />
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="deliveryOriginLine2">
                  {t("orders.settings.deliveryOriginLine2", {
                    defaultValue: "Apto / oficina (opcional)",
                  })}
                </Label>
                <Input
                  id="deliveryOriginLine2"
                  onChange={(event) => setLine2(event.target.value)}
                  value={line2 ?? ""}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deliveryOriginCity">
                  {t("orders.settings.deliveryOriginCity", { defaultValue: "Ciudad" })}
                </Label>
                <Input
                  id="deliveryOriginCity"
                  onChange={(event) => setCity(event.target.value)}
                  value={city ?? ""}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deliveryOriginCountry">
                  {t("orders.settings.deliveryOriginCountry", { defaultValue: "País" })}
                </Label>
                <Input
                  id="deliveryOriginCountry"
                  maxLength={2}
                  onChange={(event) => setCountry(event.target.value.toUpperCase())}
                  value={country}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="deliveryCoverageRadiusM">
                {t("orders.settings.deliveryCoverageRadiusM", {
                  defaultValue: "Radio de cobertura (metros)",
                })}
              </Label>
              <Input
                id="deliveryCoverageRadiusM"
                inputMode="numeric"
                onChange={(event) => setRadiusText(event.target.value)}
                placeholder="5000"
                value={radiusText}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button disabled={update.isPending} type="submit">
                {t("orders.settings.deliveryConfigSave", {
                  defaultValue: "Guardar origen y cobertura",
                })}
              </Button>
              <Button
                disabled={update.isPending || !isConfigured}
                onClick={clear}
                type="button"
                variant="outline"
              >
                {t("orders.settings.deliveryConfigClear", {
                  defaultValue: "Quitar origen y cobertura",
                })}
              </Button>
            </div>

            {!isConfigured ? (
              <p className="text-muted-foreground text-sm">
                {t("orders.settings.deliveryConfigUnconfigured", {
                  defaultValue:
                    "El envío está habilitado pero faltan el origen o la cobertura. Los clientes no podrán iniciar pedidos hasta que completes la configuración.",
                })}
              </p>
            ) : null}
          </fieldset>
        </form>
      </CardContent>
    </Card>
  );
}
